import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  IconButton,
  Paper,
  Tooltip,
  CircularProgress,
  Grow,
  alpha,
  useTheme
} from '@mui/material';
import { Reply as ReplyIcon, Close as CloseIcon, Send } from '@mui/icons-material';
import axiosInstance from '../../axiosInstance';

interface ReplyFormProps {
  postId: string;
  onReplySuccess: () => void;
}

const ReplyForm: React.FC<ReplyFormProps> = ({ postId, onReplySuccess }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [content, setContent] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const theme = useTheme();
  const isDarkTheme = theme.name === 'dark';

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!content.trim()) return;

    setIsSubmitting(true);
    try {
      const params = new URLSearchParams({
        content: content.trim()
      });

      await axiosInstance.post(
        `/forum/posts/${postId}/comments/?${params.toString()}`
      );
      
      setContent('');
      setIsOpen(false);
      onReplySuccess();
    } catch (error) {
      console.error('Error posting reply:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box sx={{ mt: 1 }} onClick={(e) => e.stopPropagation()}>
      {!isOpen ? (
        <Tooltip title="Reply to this post" placement="top" arrow>
          <Button
            onClick={handleClick}
            size="small"
            startIcon={<ReplyIcon />}
            variant="outlined"
            sx={{
              borderRadius: isDarkTheme ? '4px' : '0px',
              color: isDarkTheme ? '#64AFE3' : 'primary.main',
              borderColor: isDarkTheme ? '#64AFE3' : 'primary.main',
              '&:hover': {
                bgcolor: isDarkTheme ? alpha('#64AFE3', 0.1) : alpha(theme.palette.primary.main, 0.04),
                transform: 'translateY(-2px)',
                boxShadow: isDarkTheme ? '0 4px 8px rgba(0, 0, 0, 0.2)' : '0 2px 4px rgba(0, 0, 0, 0.1)'
              },
              transition: 'all 0.2s ease-in-out',
              textTransform: 'none'
            }}
          >
            Write a reply
          </Button>
        </Tooltip>
      ) : (
        <Grow in={isOpen} timeout={300}>
          <Paper 
            elevation={isDarkTheme ? 3 : 1}
            sx={{ 
              p: 2, 
              bgcolor: isDarkTheme ? alpha(theme.palette.background.secondary, 0.6) : 'background.secondary',
              borderRadius: isDarkTheme ? '8px' : '0px',
              border: '1px solid',
              borderColor: isDarkTheme ? alpha(theme.palette.divider, 0.2) : 'divider',
              width: '100%',
              backdropFilter: 'blur(8px)',
              position: 'relative'
            }}
          >
            <Box sx={{ 
              position: 'absolute', 
              top: '8px', 
              right: '8px',
              zIndex: 1
            }}>
              <IconButton 
                size="small" 
                onClick={(e) => {
                  e.stopPropagation();
                  setIsOpen(false);
                }}
                sx={{ 
                  color: isDarkTheme ? alpha(theme.palette.text.primary, 0.6) : 'text.secondary',
                  '&:hover': {
                    color: 'error.main',
                    bgcolor: isDarkTheme ? alpha(theme.palette.error.main, 0.1) : alpha(theme.palette.error.main, 0.04)
                  },
                  borderRadius: isDarkTheme ? '4px' : '0px',
                  transition: 'all 0.2s ease'
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
            
            <form onSubmit={handleSubmit}>
              <TextField
                fullWidth
                multiline
                rows={3}
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder="Share your thoughts..."
                variant="outlined"
                size="small"
                autoFocus
                disabled={isSubmitting}
                sx={{ 
                  mb: 2,
                  mt: 1,
                  borderRadius: isDarkTheme ? '4px' : '0px',
                  '& .MuiOutlinedInput-root': {
                    bgcolor: isDarkTheme ? alpha(theme.palette.background.paper, 0.3) : alpha(theme.palette.background.paper, 0.6),
                    borderRadius: isDarkTheme ? '4px' : '0px',
                    '& fieldset': {
                      borderColor: isDarkTheme ? alpha('#64AFE3', 0.3) : alpha(theme.palette.primary.main, 0.3)
                    },
                    '&:hover fieldset': {
                      borderColor: isDarkTheme ? '#64AFE3' : theme.palette.primary.main
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: isDarkTheme ? '#64AFE3' : theme.palette.primary.main
                    }
                  },
                  '& .MuiInputBase-input': {
                    color: isDarkTheme ? theme.palette.text.primary : theme.palette.text.primary,
                  }
                }}
              />
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                <Button
                  variant="text"
                  onClick={() => setIsOpen(false)}
                  size="small"
                  disabled={isSubmitting}
                  sx={{
                    textTransform: 'none',
                    color: isDarkTheme ? alpha(theme.palette.text.primary, 0.7) : 'text.secondary',
                    borderRadius: isDarkTheme ? '4px' : '0px',
                    '&:hover': {
                      bgcolor: isDarkTheme ? alpha(theme.palette.background.paper, 0.1) : alpha(theme.palette.background.paper, 0.3)
                    }
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  type="submit"
                  disabled={isSubmitting || !content.trim()}
                  size="small"
                  startIcon={isSubmitting ? <CircularProgress size={16} color="inherit" /> : <Send />}
                  sx={{
                    px: 3,
                    py: 0.75,
                    textTransform: 'none',
                    fontWeight: 500,
                    borderRadius: isDarkTheme ? '4px' : '0px',
                    bgcolor: isDarkTheme ? '#64AFE3' : theme.palette.primary.main,
                    color: isDarkTheme ? '#000000' : theme.palette.primary.contrastText,
                    '&:hover': {
                      bgcolor: isDarkTheme ? '#8ABFEB' : theme.palette.primary.dark,
                      transform: 'translateY(-1px)',
                      boxShadow: isDarkTheme ? '0 4px 8px rgba(0, 0, 0, 0.2)' : '0 2px 4px rgba(0, 0, 0, 0.1)'
                    },
                    transition: 'all 0.2s ease'
                  }}
                >
                  {isSubmitting ? 'Sending...' : 'Send Reply'}
                </Button>
              </Box>
            </form>
          </Paper>
        </Grow>
      )}
    </Box>
  );
};

export default ReplyForm; 